import { CircleCheck, LoaderCircle } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import type { LinkedinCheckState } from '../use-linkedin-check';

interface Props {
  check: LinkedinCheckState & { retry: () => void };
}

function initials(name: string | null): string {
  return (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

// The line under the LinkedIn field while the server verifies the profile: a spinner,
// then either the person's LinkedIn photo and name, or why it was refused.
export function LinkedinCheckStatus({ check }: Props) {
  switch (check.status) {
    case 'idle':
      return null;
    case 'checking':
    case 'pending':
      return (
        <p
          className="mt-2 flex items-center gap-2 text-muted-foreground text-xs"
          aria-live="polite"
        >
          <LoaderCircle className="size-3.5 animate-spin" />
          verificando tu perfil en linkedin…
        </p>
      );
    case 'found':
      return (
        <div className="mt-2 flex items-center gap-2 text-xs" aria-live="polite">
          <Avatar className="size-6">
            {check.photoUrl && (
              <AvatarImage
                src={check.photoUrl}
                alt={check.fullName ?? ''}
                referrerPolicy="no-referrer"
              />
            )}
            <AvatarFallback className="text-[10px]">
              {initials(check.fullName)}
            </AvatarFallback>
          </Avatar>
          {check.url ? (
            <a
              href={check.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold underline-offset-2 hover:underline"
            >
              {check.fullName}
            </a>
          ) : (
            <span className="font-semibold">{check.fullName}</span>
          )}
          <span className="flex items-center gap-1 text-primary">
            <CircleCheck className="size-3.5" />
            asociado correctamente
          </span>
        </div>
      );
    case 'failed':
      return (
        <p className="mt-2 text-destructive text-xs" aria-live="polite">
          {check.error}{' '}
          <button
            type="button"
            onClick={check.retry}
            className="underline underline-offset-2"
          >
            reintentar
          </button>
        </p>
      );
    default:
      return (
        <p className="mt-2 text-destructive text-xs" aria-live="polite">
          {check.error}
        </p>
      );
  }
}
